import React, { useState, useRef, useEffect } from "react";
import TopicSelector from "./SAK/Questionnaire/TopicSelector";
import TopicInput from "./SAK/Questionnaire/TopicInput";
import SubmitMessage from "../Shared/SubmitMessage";
import { PhraseKey } from "../../languages";

interface TourOrderQuestionnaireProps {
  onSubmit: (topic: Topic, message: string) => void;
  visible?: boolean;
  uploadedPhotoUrl: string;
  onBackToPhoto: () => void;
  onSkip: () => void;
  childName: string;
  onQuestionnaireView?: (topic: Topic) => void;
  onMessageInteractionStart?: (topic: Topic) => void;
  isSubmitting?: boolean;
  error?: string | null;
  onClearEletterError?: () => void;
}

const TOPICS = [
  {
    id: "Family",
    label: "Family",
    icon: "https://wvusstatic.com/sak/questionnaire/family.svg"
  },
  {
    id: "Pets",
    label: "Pets",
    icon: "https://wvusstatic.com/sak/questionnaire/fish_bowl.svg"
  },
  {
    id: "Sports",
    label: "Sports",
    icon: "https://wvusstatic.com/sak/questionnaire/sports.svg"
  },
  {
    id: "Other",
    label: "Other",
    icon: "https://wvusstatic.com/sak/questionnaire/other.svg"
  }
] as const;

type Topic = (typeof TOPICS)[number]["id"];

function getTopicQuestion(topic: Topic, childName = "your sponsored child"): string {
  switch (topic) {
    case "Family":
      return `Tell us about the important people in your life? <em>Feel free to add anything else you'd like to tell ${childName}.</em>`;
    case "Pets":
      return `What kind of pet(s) do you have, and what makes them special? <em>Feel free to add anything else you'd like to tell ${childName}.</em>`;
    case "Sports":
      return `What's your favorite sport? Tell ${childName} what you like best about it! <em>Feel free to add anything else you'd like to tell ${childName}.</em>`;
    case "Other":
      return `What would you like to share with ${childName} today?`;
    default:
      return "";
  }
}

const TourOrderQuestionnaire: React.FC<TourOrderQuestionnaireProps> = ({
  onSubmit,
  visible = true,
  onBackToPhoto,
  onSkip,
  childName,
  onQuestionnaireView,
  onMessageInteractionStart,
  isSubmitting = false,
  error = null,
  onClearEletterError
}) => {
  const [step, setStep] = useState<"topics" | "input">("topics");
  const [selectedTopic, setSelectedTopic] = useState<Topic | "">("");
  const [message, setMessage] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [error]);

  useEffect(() => {
    if (onClearEletterError) {
      onClearEletterError();
    }
  }, [step]);

  if (!visible) return null;

  return (
    <div className="post-checkout-flow__wrapper" ref={containerRef}>
      <div className="post-checkout-flow__header">
        <h3 className="post-checkout-flow__header-text __staple--small __staple">Add a message</h3>
      </div>

      {step === "topics" && (
        <TopicSelector
          topics={TOPICS}
          onTopicSelect={(topicId) => {
            const topic = topicId as Topic;
            setSelectedTopic(topic);
            setMessage(""); // Clear the message whenever a topic is chosen
            setStep("input");

            // Call the handler to track the view when a topic is selected
            if (onQuestionnaireView) {
              onQuestionnaireView(topic);
            }
          }}
          onBack={() => {
            onBackToPhoto();
          }}
          onSkip={onSkip}
        />
      )}

      {step === "input" && selectedTopic && (
        <>
          <TopicInput
            topic={selectedTopic}
            topicIcon={TOPICS.find((t) => t.id === selectedTopic)?.icon || ""}
            question={getTopicQuestion(selectedTopic, childName)}
            message={message}
            onMessageChange={setMessage}
            onBackToTopics={() => {
              setStep("topics");
            }}
            onMessageInteractionStart={() => {
              if (onMessageInteractionStart && selectedTopic) {
                onMessageInteractionStart(selectedTopic);
              }
            }}
            disabled={isSubmitting}
          />

          <button
            className="btn btn-primary"
            onClick={() => onSubmit(selectedTopic, message)}
            disabled={message.trim().length === 0 || isSubmitting}>
            {isSubmitting ? "Sending..." : "Submit"}
          </button>

          <button
            onClick={onSkip}
            className="btn-link"
            style={{ textAlign: "left" }}
            disabled={isSubmitting}>
            Skip message
          </button>
        </>
      )}

      {error && (
        <>
          <style>
            {`
              .submit-message.alert.questionnaire {
                margin-top: 0;
                margin-bottom: 0;
              }
            `}
          </style>
          <SubmitMessage
            visible={true}
            alertType="danger"
            messagePhraseKey={error as PhraseKey}
            wrapperClassName=""
            className="submit-message questionnaire"
          />
        </>
      )}
    </div>
  );
};

export default TourOrderQuestionnaire;
